/**
 * Saved, named give-box widgets (GH-851): Console → Payments → **Widgets**.
 *
 *   GET  /v1/organizations/:organizationId/embed-widgets                 list (+ 30-day gifts)
 *   POST /v1/organizations/:organizationId/embed-widgets                 create
 *   POST /v1/organizations/:organizationId/embed-widgets/:id             rename and/or edit
 *   POST /v1/organizations/:organizationId/embed-widgets/:id/duplicate
 *   POST /v1/organizations/:organizationId/embed-widgets/:id/archive
 *   POST /v1/organizations/:organizationId/embed-widgets/:id/restore
 *   POST /v1/organizations/:organizationId/embed-widgets/preview-token   a draft, for the preview only
 *
 * A widget is a name and a **presentation** config stored as JSON (migration 0094). The snippet an
 * organization pastes names the widget (`/embed/give/<orgId>?w=<widgetId>`, or
 * `data-givehere-widget` beside `data-givehere-org`), and the give box reads the config from the
 * server when it mints its session — so an edit here reaches every page the widget is already on,
 * without anyone re-pasting anything.
 *
 * ## Presentation only — the server decides money
 *
 * Every option below changes what the box *shows*. Where an option narrows what a donor may send —
 * the amounts when "other amount" is off, a fixed or no designation, the cover-the-fee choice when
 * it is not offered — the quote route re-reads the stored widget and refuses a request outside it
 * (`services/api/src/embed-widget-rules.ts`). Nothing here is trusted because the browser sent it.
 *
 * The bounds are spelled out rather than imported from `@givehere/domain` (kept out of the browser
 * bundles): `EMBED_WIDGET_MIN_AMOUNT_MINOR` is the $1 gift floor and `EMBED_WIDGET_MAX_AMOUNT_MINOR`
 * the $100,000 online ceiling (`MIN_DONATION_MINOR` / `MAX_DONATION_MINOR`); a test pins them equal.
 */
import { z } from "zod";
/** Frame widths, in CSS pixels. `full` fills whatever column it is pasted into. */
export const EMBED_WIDGET_SIZES = ["compact", "standard", "wide", "full"];
export const EMBED_WIDGET_WIDTHS = Object.freeze({
    compact: 320,
    standard: 400,
    wide: 560,
    full: null,
});
/** `auto` follows the visitor's own light/dark setting. */
export const EMBED_WIDGET_THEMES = ["light", "dark", "auto"];
export const EMBED_WIDGET_CORNERS = ["rounded", "square"];
/** Cover-the-fee: offered and ticked, offered and unticked, or not offered at all. */
export const EMBED_WIDGET_FEE_COVER = ["checked", "unchecked", "off"];
/** Inline: the box in the page. Popup: `embed.js` draws a button that opens the box in a dialog. */
export const EMBED_WIDGET_MODES = ["inline", "popup"];
export const EMBED_WIDGET_MAX_AMOUNTS = 6;
export const EMBED_WIDGET_MIN_AMOUNT_MINOR = 100;
export const EMBED_WIDGET_MAX_AMOUNT_MINOR = 10_000_000;
export const EMBED_WIDGET_NAME_MAX = 80;
export const EMBED_WIDGET_LABEL_MAX = 40;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Control characters and the bidi overrides a label could use to disguise itself. */
// eslint-disable-next-line no-control-regex -- refusing control characters is the point.
const UNSAFE_TEXT = /[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/;
const uuid = z
    .string()
    .regex(UUID, "not an id")
    .transform((value) => value.toLowerCase());
/** One amount: minor units as digits, within the gift floor and the online ceiling. */
const amountMinor = z
    .string()
    .regex(/^[1-9][0-9]{0,9}$/, "an amount is whole cents, as digits")
    .refine((value) => Number(value) >= EMBED_WIDGET_MIN_AMOUNT_MINOR, "Each amount must be at least $1.")
    .refine((value) => Number(value) <= EMBED_WIDGET_MAX_AMOUNT_MINOR, "Each amount must be at most $100,000.");
const safeText = (max) => z
    .string()
    .trim()
    .min(1, "Enter some text.")
    .max(max)
    .refine((value) => !UNSAFE_TEXT.test(value), "That text contains characters we can't show.");
export const embedWidgetDesignationSchema = z.discriminatedUnion("mode", [
    /** The donor picks, from what the organization offers (the ordinary picker). */
    z.object({ mode: z.literal("all") }).strict(),
    /** One designation, decided here; the picker is hidden and the server refuses any other. */
    z.object({ mode: z.literal("fixed"), designationId: uuid }).strict(),
    /** No choice: the gift goes where an unchosen gift goes (the general designation). */
    z.object({ mode: z.literal("none") }).strict(),
]);
const configShape = z
    .object({
    size: z.enum(EMBED_WIDGET_SIZES),
    theme: z.enum(EMBED_WIDGET_THEMES),
    corners: z.enum(EMBED_WIDGET_CORNERS),
    /** `rrggbb` lower case for a preset or custom tint; null = the organization's brand colour. */
    accent: z
        .string()
        .regex(/^[0-9a-fA-F]{6}$/, "a color is six hex digits")
        .transform((value) => value.toLowerCase())
        .nullable(),
    showHeader: z.boolean(),
    amountButtons: z.boolean(),
    amounts: z.array(amountMinor).max(EMBED_WIDGET_MAX_AMOUNTS, "At most six amounts."),
    otherAmount: z.boolean(),
    defaultAmountMinor: amountMinor.nullable(),
    designation: embedWidgetDesignationSchema,
    receiptEmail: z.boolean(),
    feeCover: z.enum(EMBED_WIDGET_FEE_COVER),
    /** A specific event of the organization's, or null for its general fund. */
    eventId: uuid.nullable(),
    display: z
        .object({
        mode: z.enum(EMBED_WIDGET_MODES),
        buttonLabel: safeText(EMBED_WIDGET_LABEL_MAX),
    })
        .strict(),
    /**
     * GH-877: a profile button that lets a donor with an account sign in, and a sign-in offer on
     * the card form for a donor who is not signed in. Presentation only — a gift is the same gift
     * signed in or not; signing in only links it to the donor's own receipts. **Defaults to off**,
     * so every widget saved before GH-877 (whose stored JSON has no such key) reads as off, and the
     * strict schema still refuses any key it does not define.
     */
    signIn: z.boolean().default(false),
})
    .strict();
/**
 * A widget's options. Strict: an unknown key is a 400, so nobody can come to believe a stored
 * widget can carry a fee, a total or anything else the server did not define.
 */
export const embedWidgetConfigSchema = configShape.superRefine((config, issue) => {
    if (!config.amountButtons && !config.otherAmount) {
        issue.addIssue({
            code: "custom",
            path: ["otherAmount"],
            message: "Show amount buttons, an other-amount field, or both.",
        });
    }
    if (config.amountButtons && config.amounts.length === 0) {
        issue.addIssue({ code: "custom", path: ["amounts"], message: "Add at least one amount." });
    }
    if (new Set(config.amounts).size !== config.amounts.length) {
        issue.addIssue({ code: "custom", path: ["amounts"], message: "Each amount only once." });
    }
    if (config.defaultAmountMinor !== null &&
        !config.otherAmount &&
        !(config.amountButtons && config.amounts.includes(config.defaultAmountMinor))) {
        issue.addIssue({
            code: "custom",
            path: ["defaultAmountMinor"],
            message: "Without an other-amount field, the default must be one of the buttons.",
        });
    }
});
export const DEFAULT_EMBED_WIDGET_CONFIG = Object.freeze({
    size: "standard",
    theme: "light",
    corners: "rounded",
    accent: null,
    showHeader: true,
    amountButtons: true,
    amounts: ["2500", "5000", "10000", "25000"],
    otherAmount: true,
    defaultAmountMinor: null,
    designation: { mode: "all" },
    receiptEmail: true,
    feeCover: "checked",
    eventId: null,
    display: { mode: "inline", buttonLabel: "Give" },
    signIn: false,
});
/**
 * Whether an amount is one this widget lets a donor send: one of its buttons, or anything when
 * the other-amount field is shown. The quote route asks the same question of the stored widget.
 */
export function embedWidgetAllowsAmount(config, amount) {
    if (config.otherAmount)
        return true;
    return config.amountButtons && config.amounts.includes(amount);
}
export const embedWidgetNameSchema = safeText(EMBED_WIDGET_NAME_MAX);
/* ------------------------------------------------------------------ the console's wire */
export const embedWidgetStatsSchema = z.object({
    /** Gifts still carrying money, given through this widget in the last 30 days. */
    giftCount: z.number().int().nonnegative(),
    /** Their gross, net of refunds, in minor units. */
    totalMinor: z.string().regex(/^-?[0-9]+$/),
    currency: z.string().length(3),
});
export const embedWidgetSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    name: z.string(),
    config: embedWidgetConfigSchema,
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    last30Days: embedWidgetStatsSchema,
});
export const embedWidgetListResponseSchema = z.object({
    widgets: z.array(embedWidgetSchema),
    /** Whether this session may create, edit, archive and preview drafts. */
    canEdit: z.boolean(),
});
export const createEmbedWidgetSchema = z
    .object({ name: embedWidgetNameSchema, config: embedWidgetConfigSchema.optional() })
    .strict();
export const updateEmbedWidgetSchema = z
    .object({ name: embedWidgetNameSchema.optional(), config: embedWidgetConfigSchema.optional() })
    .strict()
    .refine((body) => body.name !== undefined || body.config !== undefined, "Nothing to change.");
export const duplicateEmbedWidgetSchema = z
    .object({ name: embedWidgetNameSchema.optional() })
    .strict();
export const embedWidgetMutationResponseSchema = z.object({ widget: embedWidgetSchema });
/** A draft for the console's preview frame. `widgetId` only labels it; it changes nothing. */
export const embedWidgetPreviewRequestSchema = z
    .object({ config: embedWidgetConfigSchema, widgetId: z.string().uuid().nullable().optional() })
    .strict();
export const embedWidgetPreviewResponseSchema = z.object({
    previewToken: z.string().min(1),
    expiresAt: z.string(),
});
/* -------------------------------------------------------------- what the box is told */
/**
 * The widget as the public session mint returns it: the config and whether it is a draft preview.
 * Nothing private — the config is what the box draws, visible to anyone the box is shown to.
 */
export const embedSessionWidgetSchema = z.object({
    id: z.string().uuid().nullable(),
    config: embedWidgetConfigSchema,
    /** True for the console's draft preview: the box draws everything and takes no gift. */
    preview: z.boolean(),
});
/* -------------------------------------------------------------- the snippet's hints */
/**
 * `?w=` on the give box's address: the saved widget it draws. Anything that is not an id is
 * dropped, and the box falls back to the organization's defaults (a typo still gives).
 */
export const parseEmbedWidgetId = (search) => {
    const value = new URLSearchParams(search).get("w");
    return value && UUID.test(value) ? value.toLowerCase() : null;
};
//# sourceMappingURL=embed-widgets.js.map